export const DEFAULT_QUOTES_COLUMN_ORDER = [
    'info', 'quote', 'owner', 'customer_name', 'status', 'created_on',
    'time_in_stage', 'lines', 'comments', 'shippingdate', 'quotetotal', 'quoteaccess',
];

export const QUOTES_TABLE_COLUMN_COUNT = DEFAULT_QUOTES_COLUMN_ORDER.length;

export const NORMAL_GROUP_ID = 'none';
export const QUOTE_SET_GROUP_ID = 'quote-set';
export const QUOTE_SET_MERGED_ID = 'quoteset';
export const QUOTE_SET_SOURCE_IDS = ['quote', 'owner', 'customer_name'];

export const MAX_CUSTOM_GROUPS = 10;
export const QUOTES_MERGED_COLUMN_WIDTH = 280;

/** Display columns that can be combined. Info, Order Status, and Quote Access stay separate. */
export const GROUPABLE_COLUMN_IDS = [
    'quote', 'owner', 'customer_name', 'created_on', 'time_in_stage', 'lines',
    'shippingdate', 'quotetotal',
];

export const COLUMN_LABELS = {
    info: 'Info',
    quote: 'Quote',
    owner: 'Owner',
    customer_name: 'Customer Name',
    status: 'Order Status',
    created_on: 'Created On',
    time_in_stage: 'Time in Stage',
    lines: 'Lines #',
    // Column id stays 'comments' so saved column order and visibility keep working.
    comments: 'Sticky Notes',
    shippingdate: 'Shipping Date',
    quotetotal: 'Quote Total',
    quoteaccess: 'Quote Access',
    [QUOTE_SET_MERGED_ID]: 'Quote Set',
};

/** Pixel floors — keep in sync with .quotes-col-* widths in App.css. */
export const QUOTES_COLUMN_WIDTHS = {
    info: 120,
    quote: 150,
    owner: 150,
    customer_name: 240,
    status: 190,
    created_on: 170,
    time_in_stage: 190,
    lines: 55,
    comments: 280,
    shippingdate: 190,
    quotetotal: 180,
    quoteaccess: 200,
    [QUOTE_SET_MERGED_ID]: 300,
};

export function isMergedColumnId(columnId) {
    return columnId === QUOTE_SET_MERGED_ID || (typeof columnId === 'string' && columnId.startsWith('group-'));
}

/** Width of the quotes table for the columns that are currently visible. */
export function getQuotesTableWidth(order, visibility = {}) {
    const ids = Array.isArray(order) && order.length ? order : DEFAULT_QUOTES_COLUMN_ORDER;
    return ids.reduce((sum, id) => {
        if (visibility[id] === false) return sum;
        if (QUOTES_COLUMN_WIDTHS[id]) return sum + QUOTES_COLUMN_WIDTHS[id];
        if (isMergedColumnId(id)) return sum + QUOTES_MERGED_COLUMN_WIDTH;
        return sum;
    }, 0);
}

export const QUOTES_COLUMN_ORDER_STORAGE_KEY = 'bridgeQuotes.columnOrder.v1';

export function sanitizeColumnOrder(savedOrder, excludeColumnIds = []) {
    if (!Array.isArray(savedOrder)) return [...DEFAULT_QUOTES_COLUMN_ORDER];
    const excludeSet = new Set(excludeColumnIds);
    const valid = savedOrder.filter(
        (id) => (DEFAULT_QUOTES_COLUMN_ORDER.includes(id) || isMergedColumnId(id)) && !excludeSet.has(id)
    );
    const missing = DEFAULT_QUOTES_COLUMN_ORDER.filter((id) => !valid.includes(id) && !excludeSet.has(id));
    if (!valid.length) return [...DEFAULT_QUOTES_COLUMN_ORDER].filter((id) => !excludeSet.has(id));
    const next = [...valid];
    missing.forEach((id) => {
        if (id === 'comments') {
            const after = next.indexOf('lines');
            if (after !== -1) {
                next.splice(after + 1, 0, id);
                return;
            }
        }
        next.push(id);
    });
    return next;
}

export function getColumnGroupsStorageKey(userEmail) {
    const user = (userEmail || 'anonymous').toLowerCase().trim();
    return `bridgeQuotes.columnGroups.v1.${user}`;
}

export function getMergedIdForGroup(groupId) {
    if (groupId === QUOTE_SET_GROUP_ID) return QUOTE_SET_MERGED_ID;
    if (groupId && groupId.startsWith('custom-')) return `group-${groupId}`;
    return null;
}

export function getSourceIdsForGroup(groupId, customGroups = []) {
    if (groupId === QUOTE_SET_GROUP_ID) return [...QUOTE_SET_SOURCE_IDS];
    const custom = customGroups.find((g) => g.id === groupId);
    const ids = custom?.columnIds ? [...custom.columnIds] : [];
    return ids.filter((id) => GROUPABLE_COLUMN_IDS.includes(id));
}

export function getGroupDisplayName(groupId, customGroups = []) {
    if (groupId === NORMAL_GROUP_ID) return 'Normal View';
    if (groupId === QUOTE_SET_GROUP_ID) return 'Quote Set';
    const custom = customGroups.find((g) => g.id === groupId);
    return custom?.name || 'Custom Group';
}

export function loadColumnGroupsState(userEmail) {
    const fallback = {
        activeGroupId: NORMAL_GROUP_ID,
        defaultGroupId: NORMAL_GROUP_ID,
        customGroups: [],
    };
    try {
        const raw = localStorage.getItem(getColumnGroupsStorageKey(userEmail));
        if (!raw) return fallback;
        const parsed = JSON.parse(raw);
        const customGroups = Array.isArray(parsed.customGroups)
            ? parsed.customGroups.slice(0, MAX_CUSTOM_GROUPS).map((g) => ({
                ...g,
                columnIds: (g.columnIds || []).filter((id) => GROUPABLE_COLUMN_IDS.includes(id)),
            }))
            : [];
        return {
            activeGroupId: parsed.activeGroupId || NORMAL_GROUP_ID,
            defaultGroupId: parsed.defaultGroupId || NORMAL_GROUP_ID,
            customGroups,
        };
    } catch (e) {
        return fallback;
    }
}

export function saveColumnGroupsState(userEmail, state) {
    try {
        localStorage.setItem(getColumnGroupsStorageKey(userEmail), JSON.stringify(state));
    } catch (e) { /* ignore storage errors */ }
}

/** On each visit (refresh or a new login), open the group marked as default. */
export function getStartupColumnGroupsState(userEmail) {
    const groups = loadColumnGroupsState(userEmail);
    const startGroupId = groups.defaultGroupId || NORMAL_GROUP_ID;
    return { ...groups, activeGroupId: startGroupId };
}

/** Replace grouped source columns with a single merged virtual column. */
export function buildEffectiveColumnOrder(baseColumnOrder, groupId, customGroups = []) {
    const order = Array.isArray(baseColumnOrder) && baseColumnOrder.length
        ? [...baseColumnOrder]
        : [...DEFAULT_QUOTES_COLUMN_ORDER];

    if (!groupId || groupId === NORMAL_GROUP_ID) {
        return order.filter((id) => DEFAULT_QUOTES_COLUMN_ORDER.includes(id) || isMergedColumnId(id))
            .map((id) => (isMergedColumnId(id) ? null : id))
            .filter(Boolean)
            .concat(DEFAULT_QUOTES_COLUMN_ORDER.filter((id) => !order.includes(id)));
    }

    const sourceIds = getSourceIdsForGroup(groupId, customGroups);
    const mergedId = getMergedIdForGroup(groupId);
    if (!sourceIds.length || !mergedId) return [...order];

    const sourceSet = new Set(sourceIds);
    const result = [];
    let mergedInserted = false;

    for (const id of order) {
        if (isMergedColumnId(id)) {
            if (id === mergedId && !mergedInserted) {
                result.push(mergedId);
                mergedInserted = true;
            }
            continue;
        }
        if (sourceSet.has(id)) {
            if (!mergedInserted) {
                result.push(mergedId);
                mergedInserted = true;
            }
        } else if (DEFAULT_QUOTES_COLUMN_ORDER.includes(id)) {
            result.push(id);
        }
    }

    if (!mergedInserted) {
        const insertAt = order.findIndex((id) => sourceSet.has(id));
        const idx = insertAt >= 0 ? insertAt : result.length;
        result.splice(idx, 0, mergedId);
    }

    DEFAULT_QUOTES_COLUMN_ORDER.forEach((id) => {
        if (!sourceSet.has(id) && !result.includes(id)) result.push(id);
    });

    return result;
}

/** Expand a merged column back into its source columns (when switching to Normal View). */
export function expandMergedColumnOrder(effectiveOrder, mergedId, sourceIds) {
    if (!mergedId || !Array.isArray(effectiveOrder)) return [...DEFAULT_QUOTES_COLUMN_ORDER];
    const idx = effectiveOrder.indexOf(mergedId);
    if (idx === -1) return effectiveOrder.filter((id) => DEFAULT_QUOTES_COLUMN_ORDER.includes(id));

    const presentSources = sourceIds.filter((id) => DEFAULT_QUOTES_COLUMN_ORDER.includes(id));
    return [
        ...effectiveOrder.slice(0, idx),
        ...presentSources,
        ...effectiveOrder.slice(idx + 1).filter((id) => !isMergedColumnId(id)),
    ];
}

export function getEffectiveColumnOrder(columnOrder, activeGroupId, customGroups = []) {
    const baseOnly = sanitizeColumnOrder(
        (columnOrder || []).filter((id) => !isMergedColumnId(id))
    );

    if (!activeGroupId || activeGroupId === NORMAL_GROUP_ID) {
        return baseOnly;
    }

    const sourceIds = getSourceIdsForGroup(activeGroupId, customGroups);
    const mergedId = getMergedIdForGroup(activeGroupId);

    let order = (columnOrder || []).some(isMergedColumnId)
        ? sanitizeColumnOrder(columnOrder, sourceIds)
        : buildEffectiveColumnOrder(baseOnly, activeGroupId, customGroups);

    if (mergedId && !order.includes(mergedId)) {
        order = buildEffectiveColumnOrder(baseOnly, activeGroupId, customGroups);
    }

    return sanitizeColumnOrder(order, sourceIds);
}

/** Load saved column order from localStorage, dropping unknown ids and appending new columns. */
export function loadColumnOrder() {
    try {
        const saved = JSON.parse(localStorage.getItem(QUOTES_COLUMN_ORDER_STORAGE_KEY));
        if (Array.isArray(saved)) return sanitizeColumnOrder(saved);
    } catch (e) { /* ignore corrupt storage */ }
    return [...DEFAULT_QUOTES_COLUMN_ORDER];
}

export function getColumnVisibilityStorageKey(userEmail) {
    const user = (userEmail || 'anonymous').toLowerCase().trim();
    return `bridgeQuotes.columnVisibility.v1.${user}`;
}

/** Load per-user column visibility map. `{ [columnId]: false }` means hidden; missing/true means visible. */
export function loadColumnVisibility(userEmail) {
    try {
        const raw = localStorage.getItem(getColumnVisibilityStorageKey(userEmail));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
        return {};
    }
}

export function saveColumnVisibility(userEmail, visibility) {
    try {
        localStorage.setItem(getColumnVisibilityStorageKey(userEmail), JSON.stringify(visibility || {}));
    } catch (e) { /* ignore storage errors */ }
}
